import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import {
  apiRequestBodyBuilder,
  getLocalCurrencyStringType,
} from "./config/tableHelper";

Chart.register(ArcElement, Tooltip, Legend);

// Extended color palette for charts with many slices
const CHART_COLORS = [
  "#4e73df",
  "#1cc88a",
  "#36b9cc",
  "#f6c23e",
  "#e74a3b",
  "#858796",
  "#f8b425",
  "#5a5c69",
  "#2e59d9",
  "#17a673",
];

const CURRENCY_METRIC_KEYS = new Set([
  "cost",
  "costs",
  "average_cpc",
  "cost_per_conversion",
  "cost_per_conv",
  "spend",
  "total_budget",
  "remaining_budget",
  "spend_to_date",
  "yesterday_spend",
  "cc1",
]);

const PERCENTAGE_METRIC_KEYS = new Set([
  "ctr",
  "interaction_rate",
  "conversion_rate",
  "invalid_click_rate",
]);

const PRIMARY_KEY_ORDER = [
  "group_name",
  "campaign_name",
  "gender",
  "age_range",
  "campaign_type",
  "device",
  "bid_strategy",
  "bid_strategy_type",
  "bidStrategy",
  "bidStrategyType",
];

const DESIRED_METRIC_ORDER = [
  "impressions",
  "clicks",
  "ctr",
  "costs",
  "cost",
  "average_cpc",
  "conversions",
  "cost_per_conversion",
  "conversion_rate",
];

const ICONS = {
  copy: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`,
  mail: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>`,
  success: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>`,
  error: `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`,
  info: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0; margin-top: 2px;"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>`,
  close: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`,
  spinner: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line><style>@keyframes spin { 100% { transform: rotate(360deg); } }</style></svg>`,
};

const REPORT_STYLE_TAG = `
  <style>
    .rp-wrap { font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; background: #f8fafc; line-height: 1.6; }
    .rp-container { width: 100%; margin: 0; padding: 20px 0; box-sizing: border-box; }
    .rp-header { border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; background: #ffffff; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .rp-title { margin: 0; font-size: 26px; font-weight: 800; color: #1e293b; letter-spacing: -0.025em; }
    .rp-meta { margin-top: 4px; font-size: 14px; color: #64748b; }
    .rp-pill { display: inline-block; padding: 6px 14px; border-radius: 8px; background: #f1f5f9; color: #1e293b; font-weight: 600; font-size: 13px; }
    .rp-section { margin-top: 20px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #ffffff; }
    .rp-sectionHd { padding: 16px 20px; background: #ffffff; border-bottom: 1px solid #e2e8f0; }
    .rp-h2 { margin: 0; font-size: 20px; font-weight: 700; color: #1e293b; text-transform: uppercase; letter-spacing: 0.05em; }
    .rp-sectionBody { padding: 20px; }
    .rp-kicker { margin: 28px 0 12px 0; font-size: 18px; font-weight: 700; color: #2563eb; border-left: 4px solid #2563eb; padding-left: 12px; }
    .rp-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; border: 1px solid #e2e8f0; }
    .rp-table th { background-color: #2930a8 !important; color: #ffffff !important; text-align: center; font-size: 12px; font-weight: 600; padding: 14px 10px; text-transform: uppercase; white-space: nowrap; }
    .rp-table td { font-size: 13px; padding: 12px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; color: #0f172a; white-space: nowrap; }
    .rp-table tr:nth-child(even) td { background: #f8fafc; }
    .rp-total td { font-weight: 700; background: #f1f5f9 !important; }
    .rp-list { margin: 0; padding-left: 20px; }
    .rp-list li { margin-bottom: 10px; }
    .rp-grid { display: block; width: 100%; }
    .rp-chart { border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; background: #fff; width: 100%; margin-bottom: 20px; box-sizing: border-box; }
    .rp-chartTitle { margin-bottom: 15px; font-size: 16px; font-weight: 700; color: #1e293b; }
    .rp-table-wrap { width: 100%; border-collapse: collapse; }
    .rp-stack-td { display: table-cell; vertical-align: middle; }
    @media (max-width: 780px) { 
      .rp-stack-td { display: block !important; width: 100% !important; max-width: 100% !important; padding-right: 0 !important; padding-bottom: 16px !important; text-align: center; }
    }
  </style>
`.trim();

const MODAL_STYLES = `
  <style>
    .custom-mod-btn { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 16px; border: none; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.15s ease; box-sizing: border-box; }
    .custom-mod-btn-primary { background: #2563eb; color: #fff; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
    .custom-mod-btn-primary:hover { background: #1d4ed8; }
    .custom-mod-btn-secondary { background: #f1f5f9; color: #334155; }
    .custom-mod-btn-secondary:hover { background: #e2e8f0; }
    .custom-mod-input:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important; outline: none; }
    .custom-mod-close:hover { background: #f1f5f9; color: #0f172a !important; }
  </style>
`;

const getReportStrings = (PageType, previousDates) => {
  const isMonthly = PageType === "monthly_reporting";
  const isWeekly = PageType === "weekly_reporting";
  const isDaily = PageType === "daily_reporting";

  const title = isMonthly
    ? "Monthly Performance Report"
    : isWeekly
      ? "Weekly Performance Report"
      : isDaily
        ? "Daily Performance Report"
        : "Performance Report";

  const summary = isMonthly
    ? "Summary of account performance for last month."
    : isWeekly
      ? "Summary of account performance for last week."
      : isDaily
        ? "Summary of account performance for yesterday."
        : "Summary of account performance for the selected period.";

  const dateStr =
    previousDates?.curr_end_date &&
    previousDates?.curr_start_date !== previousDates?.curr_end_date
      ? `${previousDates.curr_start_date} to ${previousDates.curr_end_date}`
      : previousDates?.curr_start_date || "";

  return { title, summary, dateStr };
};

const formatKeyLabel = (key) => {
  if (!key) return "";
  return key
    .replace(/_/g, " ")
    .replace(/\bctr\b/gi, "CTR")
    .replace(/\binteraction rate\b/gi, "Interaction Rate")
    .replace(/\baverage cpc\b/gi, "Average CPC")
    .replace(/\bcost per conv\b/gi, "Cost/Conv")
    .replace(/\bconversion rate\b/gi, "Conversion Rate")
    .replace(/\bbid strategy type\b/gi, "Bid Strategy")
    .replace(/\bcurrent\b/gi, "Curr")
    .replace(/\bprevious\b/gi, "Prev")
    .replace(/\bpercentage difference\b/gi, "% Diff")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getFormattedValue = (key, value, currency = "INR") => {
  if (value == null) return "";
  const symbol = currency === "USD" ? "$" : "₹";
  const lowerKey = key?.toLowerCase();

  if (key && CURRENCY_METRIC_KEYS.has(lowerKey)) {
    return `${symbol} ${Number(value).toLocaleString(
      getLocalCurrencyStringType(),
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`;
  }

  if (key && PERCENTAGE_METRIC_KEYS.has(key)) {
    return `${parseFloat(value).toFixed(2)}%`;
  }

  if (typeof value === "number") {
    return Number(value).toLocaleString(getLocalCurrencyStringType());
  }

  return value;
};

const formatDiffCell = (val) => {
  if (val == null || val === "") return "0%";
  const numVal = parseFloat(val);
  if (isNaN(numVal)) {
    return typeof val === "string" && val.trim() !== "" ? val : "0%";
  }
  if (numVal > 0)
    return `<span style="color:#16a34a;">↑ +${numVal.toFixed(2)}%</span>`;
  if (numVal < 0)
    return `<span style="color:#dc2626;">↓ ${numVal.toFixed(2)}%</span>`;
  return "0%";
};

const renderSimpleTable = (dataArray, totals, currency = "INR") => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return "<p>No data available</p>";
  }

  let allKeys = [
    ...new Set(dataArray.flatMap((obj) => Object.keys(obj))),
  ].filter((k) => k.toLowerCase() !== "campaign_ids");

  allKeys.sort((a, b) => {
    const aPrimary = PRIMARY_KEY_ORDER.indexOf(a);
    const bPrimary = PRIMARY_KEY_ORDER.indexOf(b);
    if (aPrimary !== -1 && bPrimary !== -1) return aPrimary - bPrimary;
    if (aPrimary !== -1) return -1;
    if (bPrimary !== -1) return 1;

    const aMetric = DESIRED_METRIC_ORDER.indexOf(a);
    const bMetric = DESIRED_METRIC_ORDER.indexOf(b);
    if (aMetric !== -1 && bMetric !== -1) return aMetric - bMetric;

    return a.localeCompare(b);
  });

  const headerRow = allKeys
    .map((key) => `<th>${formatKeyLabel(key)}</th>`)
    .join("");
  const rows = dataArray
    .map(
      (row) =>
        `<tr>${allKeys
          .map(
            (key) =>
              `<td>${getFormattedValue(key, row[key], row.currency || currency)}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const totalRowHtml = totals
    ? `<tr class="rp-total">${allKeys
        .map((key) =>
          PRIMARY_KEY_ORDER.includes(key)
            ? `<td>Total</td>`
            : `<td>${getFormattedValue(key, totals[key], currency)}</td>`,
        )
        .join("")}</tr>`
    : "";

  return `<div style="overflow-x:auto;"><table class="rp-table"><thead><tr>${headerRow}</tr></thead><tbody>${rows}${totalRowHtml}</tbody></table></div>`;
};

const generateHtmlTable = (data, columns, currencySymbol) => {
  const visibleColumns = columns.filter((col) => col.visible);
  const headers = visibleColumns.map((col) => `<th>${col.title}</th>`).join("");

  const bodyRows = data.map((row) => {
    const isDiff = String(row.period_type).toLowerCase() === "difference";
    const cells = visibleColumns.map((col) => {
      let v = row[col.key] ?? 0;

      if (
        isDiff &&
        String(v).toLowerCase() !== "difference" &&
        col.key !== "period_type"
      ) {
        v = formatDiffCell(v);
      } else if (
        ["cost", "average_cpc", "cost_per_conversion"].includes(col.key)
      ) {
        v = `${currencySymbol}${Number(v).toLocaleString(getLocalCurrencyStringType())}`;
      } else if (
        ["ctr", "conversion_rate", "interaction_rate"].includes(col.key)
      ) {
        v = `${parseFloat(v).toFixed(2)}%`;
      } else if (typeof v === "number") {
        v = Number(v).toLocaleString(getLocalCurrencyStringType());
      }

      return `<td>${v}</td>`;
    });
    return `<tr class="${isDiff ? "rp-total" : ""}">${cells.join("")}</tr>`;
  });

  return `<div style="overflow-x:auto;"><table class="rp-table"><thead><tr>${headers}</tr></thead><tbody>${bodyRows.join("")}</tbody></table></div>`;
};

export const handleCopyHtmlTable = async ({
  previousDates,
  groupNames,
  data,
  columns,
  currencySymbol,
  categoryType,
  categoryInsightUrl,
  setCategoryInsightUrl,
  insight,
  setLoading,
  PageType,
  downloadOnly = false,
}) => {
  if (setLoading) setLoading(true);

  const customerId = localStorage.getItem("customer_id");
  const projectId = localStorage.getItem("project_id");
  const email = localStorage.getItem("email");
  const adAccountId = localStorage.getItem("ad_account_id");
  const platform = localStorage.getItem("platform");
  const isProjectLogoVisible = localStorage.getItem(
    `showLogoProject_${projectId}`,
  );

  const {
    title: reportTitle,
    summary: reportSummary,
    dateStr,
  } = getReportStrings(PageType, previousDates);

  let tableNames = [];
  let projectIconBase64 = null;
  let projectIconPrefixed = null;

  try {
    const visibilityRes = await fetch(
      "https://api.confidanto.com/table-visibility/get",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: customerId,
          project_id: projectId,
          email,
          page_type: PageType,
        }),
      },
    );
    const visibilityData = await visibilityRes.json();
    tableNames = visibilityData.map((item) => item.table_name);
  } catch (err) {
    console.error("Failed to fetch visible tables:", err);
  }

  // --- Category Data & Insights Fetching ---
  let categoryTableData = [];
  let fetchedCatInsights = [];

  try {
    const catTypeMap = {
      daily_reporting: "Daily",
      weekly_reporting: "Weekly",
      monthly_reporting: "Monthly",
    };
    const catType = catTypeMap[PageType] || categoryType || "Weekly";

    const catReqBody = {
      customer_id: customerId,
      email,
      project_id: projectId,
      ad_account_id: adAccountId,
      platform,
      start_date: previousDates?.curr_start_date,
      end_date: previousDates?.curr_end_date,
      previous_start_date: previousDates?.prev_start_date,
      previous_end_date: previousDates?.prev_end_date,
      category_type: catType,
    };

    const catDataRes = await fetch(
      "https://api.confidanto.com/campaign-group/campaign-curr-prev-group-metrcis",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(catReqBody),
      },
    );
    const catJson = await catDataRes.json();

    if (Array.isArray(catJson)) {
      categoryTableData = catJson.map((groupData) => {
        const flattened = {};
        for (const key in groupData) {
          if (
            [
              "current_totals",
              "previous_totals",
              "percentage_difference",
            ].includes(key)
          ) {
            for (const subKey in groupData[key]) {
              flattened[`${key.replace("_totals", "")}_${subKey}`] =
                groupData[key][subKey];
            }
          } else {
            flattened[key] = groupData[key];
          }
        }
        return flattened;
      });
    }

    const catInsightReqBody = apiRequestBodyBuilder(null, {
      start_date: previousDates?.curr_start_date,
      end_date: previousDates?.curr_end_date,
      insight_type: catType,
    });

    const catInsightRes = await fetch(
      "https://api.confidanto.com/get-insights/category-insights",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(catInsightReqBody),
      },
    );
    const catInsightJson = await catInsightRes.json();

    if (Array.isArray(catInsightJson)) {
      fetchedCatInsights = catInsightJson;
    }
  } catch (err) {
    console.error("Failed to fetch category data or insights:", err);
  }

  // Fallback insights mechanism if primary failed
  if (
    !fetchedCatInsights.length &&
    (!categoryInsightUrl ||
      categoryInsightUrl === "No category insights available.")
  ) {
    try {
      const res = await fetch(
        "https://api.confidanto.com/gemini-insights/category-daily-insights",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_id: customerId,
            project_id: projectId,
            email,
            start_date: previousDates.curr_start_date,
            end_date: previousDates.curr_end_date,
          }),
        },
      );
      const data = await res.json();
      if (data.insights) {
        if (setCategoryInsightUrl) setCategoryInsightUrl(data.insights);
        categoryInsightUrl = data.insights;
      }
    } catch (err) {
      console.error("Failed to fetch fallback category insights:", err);
    }
  }

  const renderChartHtml = async ({ groupNames, startDate, endDate }) => {
    try {
      const visRes = await fetch(
        "https://api.confidanto.com/table-visibility-chart/get",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: projectId,
            customer_id: customerId,
            email,
            page_type: PageType,
          }),
        },
      );
      const visData = await visRes.json();
      if (!visData || visData.length === 0) return "";

      const allTableNames = visData.map((v) => v.table_name);
      const allMetrics = visData.reduce((acc, curr) => {
        acc[curr.table_name] = curr.metric;
        return acc;
      }, {});

      const resBody = apiRequestBodyBuilder(null, {
        table_names: allTableNames,
        group_names: groupNames,
        metrics: allMetrics,
        start_date: startDate,
        end_date: endDate,
        show_project_icon: isProjectLogoVisible,
      });

      const chartRes = await fetch(
        "https://api.confidanto.com/add-table-chart-data",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(resBody),
        },
      );

      const chartJsonRaw = await chartRes.json();
      const chartResults = chartJsonRaw.results || chartJsonRaw;

      let chartsHtml = "";
      for (const { table_name, metric } of visData) {
        let chartData = [];
        let targetResult = Array.isArray(chartResults)
          ? chartResults.find(
              (r) =>
                r.name === table_name ||
                (r.data &&
                  typeof r.data === "object" &&
                  Object.keys(r.data).some((k) => k.includes(table_name))),
            )
          : chartResults;

        if (!targetResult && Array.isArray(chartResults)) {
          targetResult = chartResults[0];
        }

        if (targetResult?.data) {
          if (Array.isArray(targetResult.data)) {
            chartData = targetResult.data;
          } else {
            const chartKey =
              Object.keys(targetResult.data).find(
                (k) => k.includes(table_name) && k.endsWith("_chart_data"),
              ) ||
              Object.keys(targetResult.data).find((k) =>
                k.endsWith("_chart_data"),
              );
            chartData = targetResult.data[chartKey] || [];
          }
        }

        if (chartData.length > 0) {
          const firstItem = chartData[0];
          const labelKey = Object.keys(firstItem).find(
            (k) => typeof firstItem[k] === "string",
          );
          const values = chartData.map((item) => {
            const v = parseFloat(item[metric]) || 0;
            return v > 1 ? v : v * 100;
          });
          const labels = chartData.map((item) =>
            formatKeyLabel(item[labelKey]),
          );

          const canvas = document.createElement("canvas");
          canvas.width = 300;
          canvas.height = 300;
          const ctx = canvas.getContext("2d");

          let chartInstance;
          await new Promise((resolve) => {
            chartInstance = new Chart(ctx, {
              type: "doughnut",
              data: {
                labels,
                datasets: [
                  {
                    data: values,
                    backgroundColor: CHART_COLORS.slice(0, values.length),
                    borderWidth: 1,
                  },
                ],
              },
              options: {
                responsive: false,
                animation: { onComplete: () => resolve() },
                plugins: { legend: { display: false } },
              },
            });
          });

          const chartDataUrl = canvas.toDataURL("image/png");
          chartInstance?.destroy();

          const legendListHtml = labels
            .map((label, i) => {
              const val = getFormattedValue(
                metric,
                values[i],
                chartData[i]?.currency || "INR",
              );
              const color = CHART_COLORS[i % CHART_COLORS.length];
              return `
                <tr>
                  <td style="padding-bottom: 8px;">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc;">
                      <tr>
                        <td align="left" style="padding: 8px 14px; font-size: 13px; color: #334155;">
                          <span style="color: ${color}; font-size: 16px; margin-right: 6px; line-height: 1;">●</span> ${label}
                        </td>
                        <td align="right" style="padding: 8px 14px; font-size: 13px; font-weight: 700; color: #0f172a;">
                          ${val}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              `;
            })
            .join("");

          chartsHtml += `
            <div class="rp-chart" style="border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; background: #fff; margin-bottom: 20px; width: 100%; box-sizing: border-box;">
              <div class="rp-chartTitle" style="margin-bottom: 15px; font-size: 16px; font-weight: 700; color: #1e293b;">
                ${formatKeyLabel(table_name)} (${metric.toUpperCase()})
              </div>
              <table width="100%" cellpadding="0" cellspacing="0" border="0" class="rp-table-wrap">
                <tr>
                  <td width="250" align="center" valign="middle" class="rp-stack-td" style="width: 250px; max-width: 250px; padding-right: 20px;">
                    <img src="${chartDataUrl}" style="width: 100%; max-width: 220px; height: auto; display: block; margin: 0 auto;" />
                  </td>
                  <td valign="middle" class="rp-stack-td">
                    <table width="100%" cellpadding="0" cellspacing="0" border="0">
                      ${legendListHtml}
                    </table>
                  </td>
                </tr>
              </table>
            </div>
          `;
        }
      }
      return chartsHtml;
    } catch (err) {
      return "<p>Failed to load charts.</p>";
    }
  };

  let additionalTablesHtml = "";
  try {
    const resBody = apiRequestBodyBuilder(null, {
      start_date: previousDates.curr_start_date,
      end_date: previousDates.curr_end_date,
      table_names: tableNames,
      group_names: groupNames,
      show_project_icon: isProjectLogoVisible,
    });

    const res = await fetch("https://api.confidanto.com/add-table-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(resBody),
    });

    const jsonResponse = await res.json();
    projectIconBase64 = jsonResponse.project_icon;

    if (projectIconBase64) {
      const rawBase64 = `data:image/png;base64,${projectIconBase64}`;
      projectIconPrefixed = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const displaySize = 120;
          const crispMultiplier = 2;
          const maxW = displaySize * crispMultiplier;
          const maxH = displaySize * crispMultiplier;

          let { width, height } = img;
          if (width > maxW || height > maxH) {
            const ratio = Math.min(maxW / width, maxH / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, width, height);

          resolve({ src: canvas.toDataURL("image/png"), size: displaySize });
        };
        img.onerror = () => resolve({ src: rawBase64, size: 160 });
        img.src = rawBase64;
      });
    }

    const responseData = jsonResponse.results || jsonResponse;

    additionalTablesHtml = responseData
      .map((table) => {
        if (table.success === false) return "";
        const { name, data: tableData, totals } = table;
        if (!tableData) return "";

        if (name === "campaignType" && Array.isArray(tableData)) {
          const flatRows = tableData.map((entry) => {
            let cType = entry.campaign_type;
            let d = entry;
            if (!cType) {
              cType = Object.keys(entry)[0];
              d = entry[cType] || {};
            }
            return {
              campaign_type: cType,
              spend: d.cost || 0,
              total_budget: d.total_budget || 0,
              remaining_budget: d.remaining_budget || 0,
              impressions: d.impressions || 0,
              leads: d.conversion || 0,
              clicks: d.clicks || 0,
              ctr: d.ctr || 0,
              yesterday_spend: d.yesterday_spend || 0,
              currency: d.currency || "INR",
            };
          });

          const curr = flatRows[0]?.currency || "INR";
          return `
            <div class="rp-kicker">${formatKeyLabel(name)}</div>
            <div style="overflow-x:auto;">
              <table class="rp-table">
                <thead>
                  <tr><th>Campaign Type</th><th>Spend</th><th>Total Budget</th><th>Remaining Budget</th><th>Impressions</th><th>Leads</th><th>Clicks</th><th>CTR</th><th>Yesterday Spend</th></tr>
                </thead>
                <tbody>
                  ${flatRows
                    .map(
                      (r) =>
                        `<tr><td>${r.campaign_type}</td><td>${getFormattedValue("spend", r.spend, curr)}</td><td>${getFormattedValue("total_budget", r.total_budget, curr)}</td><td>${getFormattedValue("remaining_budget", r.remaining_budget, curr)}</td><td>${getFormattedValue("impressions", r.impressions)}</td><td>${getFormattedValue("leads", r.leads)}</td><td>${getFormattedValue("clicks", r.clicks)}</td><td>${getFormattedValue("ctr", r.ctr)}</td><td>${getFormattedValue("yesterday_spend", r.yesterday_spend, curr)}</td></tr>`,
                    )
                    .join("")}
                  <tr class="rp-total"><td>Total</td><td>${getFormattedValue("spend", totals?.cost, curr)}</td><td>${getFormattedValue("total_budget", totals?.total_budget, curr)}</td><td>${getFormattedValue("remaining_budget", totals?.remaining_budget, curr)}</td><td>${getFormattedValue("impressions", totals?.impressions)}</td><td>${getFormattedValue("leads", totals?.conversion)}</td><td>${getFormattedValue("clicks", totals?.clicks)}</td><td>${getFormattedValue("ctr", totals?.ctr)}</td><td>${getFormattedValue("yesterday_spend", totals?.yesterday_spend, curr)}</td></tr>
                </tbody>
              </table>
            </div>`;
        }

        return `<div class="rp-kicker">${formatKeyLabel(name)}</div>${renderSimpleTable(
          Array.isArray(tableData) ? tableData : [tableData],
          totals,
        )}`;
      })
      .join("");
  } catch (err) {
    additionalTablesHtml = "<p>Failed to load detailed tables.</p>";
  }

  const htmlTable = generateHtmlTable(data, columns, currencySymbol);

  // --- Category HTML section ---
  let newCategoryTableHtml = "";
  if (categoryTableData.length > 0) {
    const customCategoryColumns = [
      { key: "group_name", label: "Group Name" },
      { key: "current_clicks", label: "Clicks" },
      { key: "percentage_difference_clicks", label: "Clicks % Δ" },
      { key: "current_impressions", label: "Impr." },
      { key: "percentage_difference_impressions", label: "Impr. % Δ" },
      { key: "current_cost", label: "Cost" },
      { key: "percentage_difference_cost", label: "Cost % Δ" },
      { key: "current_ctr", label: "CTR" },
      { key: "percentage_difference_ctr", label: "CTR % Δ" },
    ];

    const catHeader = customCategoryColumns
      .map((c) => `<th>${c.label}</th>`)
      .join("");
    const catRows = categoryTableData
      .map((row) => {
        const cells = customCategoryColumns.map((col) => {
          const k = col.key;
          const val = row[k];

          if (val == null) return `<td>-</td>`;
          if (k === "group_name")
            return `<td style="text-align:left;">${val}</td>`;
          if (k.includes("percentage_difference"))
            return `<td>${formatDiffCell(val)}</td>`;
          if (k === "current_ctr") return `<td>${Number(val).toFixed(2)}%</td>`;
          if (k.includes("cost")) {
            return `<td>${currencySymbol}${Number(val).toLocaleString(
              getLocalCurrencyStringType(),
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}</td>`;
          }
          return `<td>${Number(val).toLocaleString(getLocalCurrencyStringType())}</td>`;
        });
        return `<tr>${cells.join("")}</tr>`;
      })
      .join("");

    const totalCurrentClicks = categoryTableData.reduce(
      (acc, row) => acc + (parseFloat(row.current_clicks) || 0),
      0,
    );
    const totalPrevClicks = categoryTableData.reduce(
      (acc, row) => acc + (parseFloat(row.previous_clicks) || 0),
      0,
    );
    const totalClicksDiff = totalPrevClicks
      ? ((totalCurrentClicks - totalPrevClicks) / totalPrevClicks) * 100
      : 0;

    const totalCurrentImpr = categoryTableData.reduce(
      (acc, row) => acc + (parseFloat(row.current_impressions) || 0),
      0,
    );
    const totalPrevImpr = categoryTableData.reduce(
      (acc, row) => acc + (parseFloat(row.previous_impressions) || 0),
      0,
    );
    const totalImprDiff = totalPrevImpr
      ? ((totalCurrentImpr - totalPrevImpr) / totalPrevImpr) * 100
      : 0;

    const totalCurrentCost = categoryTableData.reduce(
      (acc, row) => acc + (parseFloat(row.current_cost) || 0),
      0,
    );
    const totalPrevCost = categoryTableData.reduce(
      (acc, row) => acc + (parseFloat(row.previous_cost) || 0),
      0,
    );
    const totalCostDiff = totalPrevCost
      ? ((totalCurrentCost - totalPrevCost) / totalPrevCost) * 100
      : 0;

    const totalCurrentCtr = totalCurrentImpr
      ? (totalCurrentClicks / totalCurrentImpr) * 100
      : 0;
    const totalPrevCtr = totalPrevImpr
      ? (totalPrevClicks / totalPrevImpr) * 100
      : 0;
    const totalCtrDiff = totalPrevCtr
      ? ((totalCurrentCtr - totalPrevCtr) / totalPrevCtr) * 100
      : 0;

    const totalRowHtml = `
      <tr class="rp-total">
        <td style="text-align:left;">Total</td>
        <td>${totalCurrentClicks.toLocaleString(getLocalCurrencyStringType())}</td>
        <td>${formatDiffCell(totalClicksDiff)}</td>
        <td>${totalCurrentImpr.toLocaleString(getLocalCurrencyStringType())}</td>
        <td>${formatDiffCell(totalImprDiff)}</td>
        <td>${currencySymbol}${totalCurrentCost.toLocaleString(
          getLocalCurrencyStringType(),
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )}</td>
        <td>${formatDiffCell(totalCostDiff)}</td>
        <td>${totalCurrentCtr.toFixed(2)}%</td>
        <td>${formatDiffCell(totalCtrDiff)}</td>
      </tr>
    `;

    newCategoryTableHtml = `
      <div class="rp-section">
        <div class="rp-sectionHd"><div class="rp-h2">Category Performance</div></div>
        <div class="rp-sectionBody">
          <div style="overflow-x:auto;">
            <table class="rp-table" style="min-width: max-content;">
              <thead><tr>${catHeader}</tr></thead>
              <tbody>
                ${catRows}
                ${totalRowHtml}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  let combinedCatInsightsHtml = "";
  if (fetchedCatInsights.length > 0) {
    combinedCatInsightsHtml += `
      <ul class="rp-list">
        ${fetchedCatInsights
          .map((i) => {
            const txt = (i.insight_text || "").replace(
              /(\$?\d+(?:[.,]\d+)?%?)/g,
              "<strong>$1</strong>",
            );
            return `<li><strong>${i.category}:</strong> ${txt}</li>`;
          })
          .join("")}
      </ul>
    `;
  } else if (
    Array.isArray(categoryInsightUrl) &&
    categoryInsightUrl.length > 0
  ) {
    combinedCatInsightsHtml += `
      <ul class="rp-list">
        ${categoryInsightUrl
          .map(
            (i) =>
              `<li><strong>${i.category}:</strong> ${i.summary || i.insight_text || ""}</li>`,
          )
          .join("")}
      </ul>
    `;
  }

  const finalChartsHtml = await renderChartHtml({
    groupNames,
    startDate: previousDates.curr_start_date,
    endDate: previousDates.curr_end_date,
  });

  const projectIconHtml = projectIconPrefixed
    ? `<img src="${projectIconPrefixed.src}" alt="Project Logo" width="${projectIconPrefixed.size}" style="display: block; width: ${projectIconPrefixed.size}px; max-width: 100%; height: auto; object-fit: contain;">`
    : "";

  const fullHtml = `
    ${REPORT_STYLE_TAG}
    <div class="rp-wrap">
      <div class="rp-container">
        <div class="rp-header">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; border-collapse: collapse;">
            <tr>
              <td width="30%" align="left" valign="middle">
                ${projectIconHtml}
              </td>
              <td width="40%" align="center" valign="middle" style="text-align: center;">
                <h1 class="rp-title" style="margin: 0; font-size: 24px; font-weight: 800; color: #1e293b;">${reportTitle}</h1>
                <div class="rp-meta" style="margin-top: 4px; font-size: 14px; color: #64748b;">${reportSummary}</div>
              </td>
              <td width="30%" align="right" valign="middle">
                <div class="rp-pill" style="display: inline-block; padding: 6px 14px; border-radius: 8px; background: #f1f5f9; color: #1e293b; font-weight: 600; font-size: 13px;">${dateStr}</div>
              </td>
            </tr>
          </table>
        </div>
        
        <div class="rp-section">
          <div class="rp-sectionHd"><div class="rp-h2">Overall Performance</div></div>
          <div class="rp-sectionBody">${htmlTable}</div>
        </div>

        <div class="rp-section">
          <div class="rp-sectionHd"><div class="rp-h2">Overall Insights</div></div>
          <div class="rp-sectionBody">
            ${
              insight
                ? `<ul class="rp-list">${insight
                    .split(/(?<=\.|\n)\s+/)
                    .filter((p) => p.trim().length > 0)
                    .map(
                      (s) =>
                        `<li>${s.replace(/(\d+(?:\.\d+)?%?)/g, "<strong>$1</strong>")}</li>`,
                    )
                    .join("")}</ul>`
                : "<p>No specific insights for this period.</p>"
            }
          </div>
        </div>

        ${newCategoryTableHtml}

        ${
          combinedCatInsightsHtml
            ? `
            <div class="rp-section">
              <div class="rp-sectionHd"><div class="rp-h2">Category Insights</div></div>
              <div class="rp-sectionBody">${combinedCatInsightsHtml}</div>
            </div>`
            : ""
        }

        <div class="rp-section">
          <div class="rp-sectionHd"><div class="rp-h2">Data Visualizations</div></div>
          <div class="rp-sectionBody"><div class="rp-grid">${finalChartsHtml || "<p>No visual data available.</p>"}</div></div>
        </div>
        
        <div class="rp-section">
          <div class="rp-sectionHd"><div class="rp-h2">Detailed Tables</div></div>
          <div class="rp-sectionBody">${additionalTablesHtml}</div>
        </div>
      </div>
    </div>
  `.trim();

  if (downloadOnly) {
    if (setLoading) setLoading(false);
    return fullHtml;
  }

  await new Promise((resolve) => {
    const overlay = document.createElement("div");
    Object.assign(overlay.style, {
      position: "fixed",
      top: "0",
      left: "0",
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.4)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: "99999",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      opacity: "0",
      transition: "opacity 0.2s ease-in-out",
    });

    const modal = document.createElement("div");
    Object.assign(modal.style, {
      background: "#ffffff",
      borderRadius: "16px",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
      width: "440px",
      maxWidth: "92%",
      overflow: "hidden",
      transform: "scale(0.95)",
      transition: "transform 0.2s ease-in-out",
    });

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
      modal.style.transform = "scale(1)";
    });

    const closeModal = () => {
      overlay.style.opacity = "0";
      modal.style.transform = "scale(0.95)";
      setTimeout(() => {
        if (document.body.contains(overlay)) {
          document.body.removeChild(overlay);
        }
        resolve();
      }, 200);
    };

    const renderInitial = () => {
      modal.innerHTML = `
        ${MODAL_STYLES}
        <div style="padding: 24px; position: relative;">
          <button id="btn-close-top" class="custom-mod-close" style="position: absolute; top: 16px; right: 16px; background: transparent; border: none; cursor: pointer; color: #94a3b8; padding: 6px; border-radius: 6px; display: flex; align-items: center; justify-content: center; transition: all 0.15s ease;">
            ${ICONS.close}
          </button>
          <div style="text-align: center;">
            <h3 style="margin: 0 0 8px 0; color: #0f172a; font-size: 20px; font-weight: 700;">Share Report</h3>
            <p style="color: #64748b; font-size: 14px; margin: 0 0 28px 0; line-height: 1.5;">Choose how you want to share this performance report.</p>
            
            <div style="display: flex; flex-direction: column; gap: 12px;">
              <button id="btn-copy" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%; border: 1px solid #e2e8f0;">
                ${ICONS.copy} Copy HTML to Clipboard
              </button>
              <button id="btn-email" class="custom-mod-btn custom-mod-btn-primary" style="width: 100%;">
                ${ICONS.mail} Send via Email
              </button>
            </div>
          </div>
        </div>
      `;

      document.getElementById("btn-close-top").onclick = closeModal;

      document.getElementById("btn-copy").onclick = async () => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              "text/html": new Blob([fullHtml], { type: "text/html" }),
              "text/plain": new Blob([fullHtml.replace(/<[^>]+>/g, "")], {
                type: "text/plain",
              }),
            }),
          ]);
          modal.innerHTML = `
            <div style="padding: 40px 24px; text-align: center;">
              <div style="display: flex; justify-content: center; margin-bottom: 16px;">${ICONS.success}</div>
              <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Copied!</h3>
              <p style="color: #64748b; font-size: 14px; margin: 0;">The report has been copied to your clipboard.</p>
            </div>
          `;
          setTimeout(closeModal, 1800);
        } catch (err) {
          modal.innerHTML = `
            <div style="padding: 40px 24px; text-align: center;">
              <div style="display: flex; justify-content: center; margin-bottom: 16px;">${ICONS.error}</div>
              <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Copy Failed</h3>
              <p style="color: #64748b; font-size: 14px; margin: 0;">Please check your browser permissions.</p>
            </div>
          `;
          setTimeout(closeModal, 2000);
        }
      };

      document.getElementById("btn-email").onclick = renderEmailInput;
    };

    const renderEmailInput = () => {
      modal.innerHTML = `
        ${MODAL_STYLES}
        <div style="padding: 24px;">
          <h3 style="margin: 0 0 8px 0; color: #0f172a; font-size: 20px; font-weight: 700;">Send via Email</h3>
          <p style="color: #64748b; font-size: 14px; margin: 0 0 20px 0; line-height: 1.5;">Enter the recipient's email address below.</p>
          
          <div style="margin-bottom: 24px;">
            <label style="display: block; text-align: left; font-size: 13px; font-weight: 600; color: #334155; margin-bottom: 6px;">Email Address</label>
            <input type="email" id="email-input" class="custom-mod-input" placeholder="client@example.com" style="width: 100%; padding: 12px 14px; border: 1px solid #cbd5e1; border-radius: 8px; box-sizing: border-box; font-size: 14px; outline: none; transition: all 0.2s; color: #0f172a;" />
          </div>

          <div style="display: flex; gap: 12px;">
            <button id="btn-cancel-email" class="custom-mod-btn custom-mod-btn-secondary" style="flex: 1;">Back</button>
            <button id="btn-send" class="custom-mod-btn custom-mod-btn-primary" style="flex: 1;">Send Report</button>
          </div>
        </div>
      `;

      const emailInput = document.getElementById("email-input");
      setTimeout(() => emailInput.focus(), 50);

      document.getElementById("btn-cancel-email").onclick = renderInitial;

      emailInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") document.getElementById("btn-send").click();
      });

      document.getElementById("btn-send").onclick = async () => {
        const inputVal = emailInput.value.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!inputVal || !emailRegex.test(inputVal)) {
          emailInput.style.borderColor = "#ef4444";
          emailInput.style.boxShadow = "0 0 0 3px rgba(239, 68, 68, 0.1)";
          return;
        }

        modal.innerHTML = `
          <div style="padding: 48px 24px; text-align: center;">
            <div style="display: flex; justify-content: center; margin-bottom: 20px;">${ICONS.spinner}</div>
            <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 18px;">Sending Report...</h3>
            <p style="color: #64748b; font-size: 14px; margin: 0;">Please wait a moment.</p>
          </div>
        `;

        try {
          const emailRes = await fetch(
            "https://api.confidanto.com/send-report",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                to: inputVal,
                subject: `${reportTitle} - ${dateStr}`,
                html: fullHtml,
              }),
            },
          );
          const eData = await emailRes.json();

          if (eData.success) {
            modal.innerHTML = `
              ${MODAL_STYLES}
              <div style="padding: 24px;">
                <div style="display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 24px;">
                  <div style="margin-bottom: 16px;">${ICONS.success}</div>
                  <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px; font-weight: 700;">Report Sent Successfully!</h3>
                  <p style="color: #64748b; font-size: 14px; margin: 0;">The report was delivered to <strong>${inputVal}</strong></p>
                </div>
                
                <div style="background: #f0fdf4; border: 1px solid #bbf7d0; padding: 12px 16px; border-radius: 8px; display: flex; align-items: flex-start; gap: 10px; margin-bottom: 24px;">
                  <div style="color: #16a34a;">${ICONS.info}</div>
                  <div style="font-size: 13px; color: #166534; line-height: 1.5; text-align: left;">
                    <strong>Note:</strong> We have dispatched this report via our official system email address.
                  </div>
                </div>

                <button id="btn-close-final" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%;">Close Window</button>
              </div>
            `;
          } else {
            modal.innerHTML = `
              ${MODAL_STYLES}
              <div style="padding: 24px; text-align: center;">
                <div style="display: flex; justify-content: center; margin-bottom: 16px;">${ICONS.error}</div>
                <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Delivery Failed</h3>
                <p style="color: #64748b; font-size: 14px; margin: 0 0 24px 0;">We could not send the email at this time. Please try again later.</p>
                <button id="btn-close-final" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%;">Close Window</button>
              </div>
            `;
          }
          document.getElementById("btn-close-final").onclick = closeModal;
        } catch (err) {
          modal.innerHTML = `
            ${MODAL_STYLES}
            <div style="padding: 24px; text-align: center;">
              <div style="display: flex; justify-content: center; margin-bottom: 16px;">${ICONS.error}</div>
              <h3 style="color: #0f172a; margin: 0 0 8px 0; font-size: 20px;">Network Error</h3>
              <p style="color: #64748b; font-size: 14px; margin: 0 0 24px 0;">An unexpected error occurred while sending.</p>
              <button id="btn-close-final" class="custom-mod-btn custom-mod-btn-secondary" style="width: 100%;">Close Window</button>
            </div>
          `;
          document.getElementById("btn-close-final").onclick = closeModal;
        }
      };
    };

    renderInitial();
  });

  if (setLoading) setLoading(false);
};
